import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
    type ReactNode,
} from "react";

interface PromptCandidate {
    eligible: boolean;
    priority: number;
}

interface AutomaticSessionPromptContextValue {
    activePromptId: string | null;
    removeCandidate: (id: string) => void;
    upsertCandidate: (id: string, candidate: PromptCandidate) => void;
}

const AutomaticSessionPromptContext =
    createContext<AutomaticSessionPromptContextValue | null>(null);

export function AutomaticSessionPromptProvider({
    children,
}: {
    children: ReactNode;
}) {
    const [candidates, setCandidates] = useState<
        Record<string, PromptCandidate>
    >({});

    const upsertCandidate = useCallback(
        (id: string, candidate: PromptCandidate) => {
            setCandidates((current) => {
                const existing = current[id];
                if (
                    existing?.eligible === candidate.eligible &&
                    existing.priority === candidate.priority
                ) {
                    return current;
                }
                return { ...current, [id]: candidate };
            });
        },
        [],
    );
    const removeCandidate = useCallback((id: string) => {
        setCandidates((current) => {
            if (!(id in current)) return current;
            const next = { ...current };
            delete next[id];
            return next;
        });
    }, []);

    const activePromptId = useMemo(() => {
        return (
            Object.entries(candidates)
                .filter(([, candidate]) => candidate.eligible)
                .sort(
                    ([firstId, first], [secondId, second]) =>
                        second.priority - first.priority ||
                        firstId.localeCompare(secondId),
                )[0]?.[0] ?? null
        );
    }, [candidates]);

    const value = useMemo(
        () => ({ activePromptId, removeCandidate, upsertCandidate }),
        [activePromptId, removeCandidate, upsertCandidate],
    );

    return (
        <AutomaticSessionPromptContext.Provider value={value}>
            {children}
        </AutomaticSessionPromptContext.Provider>
    );
}

export function useAutomaticSessionPrompt({
    eligible,
    id,
    priority,
}: PromptCandidate & { id: string }) {
    const context = useContext(AutomaticSessionPromptContext);
    const activePromptId = context?.activePromptId;
    const removeCandidate = context?.removeCandidate;
    const upsertCandidate = context?.upsertCandidate;

    useEffect(() => {
        if (!upsertCandidate || !removeCandidate) return;
        upsertCandidate(id, { eligible, priority });
        return () => removeCandidate(id);
    }, [eligible, id, priority, removeCandidate, upsertCandidate]);

    // AIDEV-NOTE: Consumers outside ProtectedRoute retain their prior standalone behavior.
    return context ? activePromptId === id : eligible;
}
